import { AxiosResponse } from 'axios';

import {
  CompanyRoleDTO,
  CompanyRolesResponseDTO,
  GetStaffResponse,
  ListStaffRequest,
  PermissionCatalogueModuleDTO,
  UpdateStaffPermissionsRole,
} from '@vyce/core/src/api/types';
import { StaffListItem } from '@vyce/core/src/types';
import {
  getExistingResourceIds,
  getModuleResourceKind,
  getResourceIdsByRole,
  getStoredResourceIds,
  pickModuleRoles,
  pickStaffModuleRoleName,
  RESOURCE_KIND_LABELS,
  requiresResourceSelection,
  StaffModuleResourceAccess,
  StaffModuleResources,
} from '@vyce/core/src/utils/staffPermissions';

import { ModuleDraft, VisibleStaffModule } from '../types';
import { CatalogueIndex } from './permissionState';
import { isComingSoonModule } from './staffModules';

// Pure role-draft logic shared by the inline roles editor and the Add Admin dialog,
// so the two can't drift on defaults, scope seeding or validation.

export type CompanyDraft = Record<string, ModuleDraft>;

export const COMPANY_MODULE = 'companies';
const HIDDEN_MODULES = ['interface'];

export const seedResourceIds = (
  role: CompanyRoleDTO | null,
  availableResources: { id: string }[],
  resourcesMap: Record<string, string[]>
) => {
  if (!role || !requiresResourceSelection(role)) return [];
  const availableIds = availableResources.map(item => item.id);
  // Nothing in the inventory: keep whatever is stored against the person, so a save
  // triggered by another module can't quietly strip this module's scope.
  if (!availableIds.length) return getStoredResourceIds(role, resourcesMap);
  const existingIds = getExistingResourceIds(role, availableIds, resourcesMap);
  // Same fallback as the Edit Permissions dialog: no stored selection → all.
  return existingIds.length ? existingIds : availableIds;
};

interface BuildVisibleModulesArgs {
  catalogueModules: PermissionCatalogueModuleDTO[];
  catalogueIndex: CatalogueIndex;
  rolesData: CompanyRolesResponseDTO;
  moduleResources: StaffModuleResources;
  readable: Required<StaffModuleResourceAccess>;
  isInternalAdmin: boolean;
  /** Keeps a module that has no assignable role — the editor passes "has an overrides row". */
  keepWithoutRoles?: (systemName: string) => boolean;
}

// The module list is backend-driven: the catalogue is the source of labels and
// groups, and a module is shown when it has an assignable role (or whatever
// `keepWithoutRoles` allows — cross-module permissions land in another module's role).
export const buildVisibleModules = ({
  catalogueModules,
  catalogueIndex,
  rolesData,
  moduleResources,
  readable,
  isInternalAdmin,
  keepWithoutRoles = () => false,
}: BuildVisibleModulesArgs): VisibleStaffModule[] =>
  catalogueModules
    .filter(module => !HIDDEN_MODULES.includes(module.module_system_name))
    .filter(module => !isComingSoonModule(module.module_system_name))
    .map(module => {
      const roles = pickModuleRoles(rolesData, module.module_display_name);
      const resourceKind = getModuleResourceKind(module.module_system_name, roles);
      const resources = resourceKind ? moduleResources[resourceKind] : [];
      // An unreadable inventory arrives empty too, so split the two: one is a
      // company that hasn't created its first team, the other is a viewer who
      // isn't allowed to look.
      const unavailable = !!resourceKind && !readable[resourceKind];
      return {
        systemName: module.module_system_name,
        label: module.module_display_name,
        roles,
        resourceKind,
        resources,
        resourceLabel: resourceKind ? RESOURCE_KIND_LABELS[resourceKind] : undefined,
        emptyInventory: !!resourceKind && !unavailable && resources.length === 0,
        inventoryUnavailable: unavailable,
        groups: catalogueIndex.groupsByModule.get(module.module_system_name) || [],
        permissions: catalogueIndex.byModule.get(module.module_system_name) || [],
      };
    })
    .filter(module => module.roles.length > 0 || keepWithoutRoles(module.systemName))
    // A module with nothing to scope to yet still shows: a role assigned now
    // costs nothing and saves a second trip once the first team exists. Only a
    // module the viewer can't read the inventory of is hidden from an employer —
    // they could neither see nor set its scope.
    .filter(module => !module.inventoryUnavailable || isInternalAdmin);

/**
 * Every module one role pick reassigns. Dialog parity: assigning a Company role
 * from "No access" defaults the remaining unassigned modules to their Admin role.
 * Never overwrites roles that are already set.
 */
export const getRoleReassignments = (
  draft: CompanyDraft,
  moduleKey: string,
  role: CompanyRoleDTO | null,
  visibleModules: VisibleStaffModule[]
) => {
  const reassigned = new Map<string, CompanyRoleDTO | null>([[moduleKey, role]]);
  if (moduleKey === COMPANY_MODULE && role && !draft[COMPANY_MODULE]?.role) {
    visibleModules.forEach(module => {
      if (module.systemName === COMPANY_MODULE) return;
      const current = draft[module.systemName];
      if (current?.role || current?.unresolvedRoleName) return;
      const adminRole = module.roles.find(item => item.role_name === 'Admin');
      if (adminRole) reassigned.set(module.systemName, adminRole);
    });
  }
  return reassigned;
};

export const applyRoleReassignments = (
  draft: CompanyDraft,
  reassigned: Map<string, CompanyRoleDTO | null>,
  visibleModules: VisibleStaffModule[],
  storedResources: Record<string, string[]>
): CompanyDraft => {
  const next: CompanyDraft = { ...draft };
  reassigned.forEach((nextRole, systemName) => {
    const module = visibleModules.find(item => item.systemName === systemName);
    next[systemName] = {
      role: nextRole,
      resourceIds: seedResourceIds(nextRole, module?.resources || [], storedResources),
      unresolvedRoleName: undefined,
    };
  });
  return next;
};

export const getDraftInvalidReason = (draft: CompanyDraft, visibleModules: VisibleStaffModule[]) => {
  for (const module of visibleModules) {
    const current = draft[module.systemName];
    if (!current) continue;
    if (current.unresolvedRoleName) {
      return `Role “${current.unresolvedRoleName}” is no longer available for ${module.label}. Pick a role or set No access.`;
    }
    // Only a module that *has* an inventory can be mis-scoped. With nothing to
    // pick from, an empty scope is the only honest answer — saving it assigns the
    // role now and leaves the scope to be set when the first one is created.
    if (
      current.role &&
      requiresResourceSelection(current.role) &&
      current.resourceIds.length === 0 &&
      module.resources.length > 0
    ) {
      const kind = (module.resourceLabel || 'items').toLowerCase();
      return `Pick at least one of the ${kind} for ${module.label} — otherwise this user will see nothing in this module.`;
    }
  }
  return null;
};

/**
 * The `roles` array of the staff permissions / staff invite payloads. `emptyScopeLabels`
 * names the modules saved with no resources, so a rejection can say which module.
 */
export const buildRolesPayload = (
  draft: CompanyDraft,
  visibleModules: VisibleStaffModule[],
  companyId: string
) => {
  const emptyScopeLabels: string[] = [];
  const roles: UpdateStaffPermissionsRole[] = [];
  visibleModules.forEach(module => {
    const current = draft[module.systemName];
    if (!current?.role) return;
    const resourceIds = getResourceIdsByRole(current.role, current.resourceIds, companyId);
    if (!resourceIds.length) emptyScopeLabels.push(module.label);
    roles.push({
      role_id: current.role.role_id,
      resource_type: current.role.resource_type,
      resource_ids: resourceIds,
    });
  });
  return { roles, emptyScopeLabels };
};

/**
 * The person's row on the company's staff list, or undefined when they hold no position
 * there. /staff has no lookup by id, so search by last name and fall back to the full list.
 */
export const findStaffMember = async (
  getStaffRequest: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>,
  companyId: string,
  userId: string,
  lastName?: string
): Promise<StaffListItem | undefined> => {
  const res = await getStaffRequest(companyId, { substring: lastName || '', limit: 100, offset: 0 });
  const found = (res.data.items || []).find(item => item.user_id === userId);
  if (found || !lastName) return found;
  const retryRes = await getStaffRequest(companyId, { substring: '', limit: 1000, offset: 0 });
  return (retryRes.data.items || []).find(item => item.user_id === userId);
};

/** The draft a staff row already holds — or an all-"No access" draft for someone new. */
export const draftFromStaffItem = (
  visibleModules: VisibleStaffModule[],
  staffItem?: Pick<StaffListItem, 'modules' | 'resources'>
): CompanyDraft => {
  const draft: CompanyDraft = {};
  visibleModules.forEach(module => {
    const roleName = staffItem ? pickStaffModuleRoleName(staffItem.modules, module.label) : undefined;
    const role = roleName ? module.roles.find(item => item.role_name === roleName) || null : null;
    draft[module.systemName] = {
      role,
      resourceIds: seedResourceIds(role, module.resources, staffItem?.resources || {}),
      unresolvedRoleName: roleName && !role ? roleName : undefined,
    };
  });
  return draft;
};
